<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { FunctionalComponent, SVGAttributes } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import dtwinsService from "~/modules/dtwin/services/dtwin.service";
import { canUpdateDtwinLifeCycleState } from "~/modules/dtwin/utils/canUpdateDtwinLifeCycleState";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";
import ILxcLightSlash from "~icons/lxc-light/slash";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

interface ActionConfiguration {
  icon: FunctionalComponent<SVGAttributes>;
  title: string;
  content: string;
}

const props = defineProps<{
  context?: DtwinListDisplayContext;
  isRemovingFromFleet?: boolean;
  isAllowedToManageFleet?: boolean;
  dtwin: Dtwin;
  dtwinModel?: DtwinModel;
}>();

const emits = defineEmits(["removeFromFleet", "lifeCycleStateUpdated"]);

const { t } = useI18n();

const { can } = useAcl();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const canUpdateLifeCycleState = computed(() => {
  // cannot update life cycle state if the user does not has the required permissions
  if (!can(ACLRoles.CAN_WRITE_DEVICE)) {
    return false;
  }

  return canUpdateDtwinLifeCycleState(props.dtwin, props.dtwinModel);
});

const showConfirm = ref(false);
const isUpdatingLifeCycleState = ref(false);

const actionConfiguration: ComputedRef<ActionConfiguration> = computed(() => {
  if (props.dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED) {
    return {
      icon: ILxcLightRotateCw,
      title: t("dtwins.action.enable"),
      content: t("dtwins.action.button.enable"),
    };
  } else {
    return {
      icon: ILxcLightSlash,
      title: t("dtwins.action.disable"),
      content: t("dtwins.action.button.disable"),
    };
  }
});

const onTriggerAction = () => {
  if (
    props.dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED &&
    props.dtwin.previousLifeCycleState
  ) {
    updateDtwinLifeCycleState(props.dtwin.previousLifeCycleState);
  } else if (
    props.dtwin.lifeCycleState === DtwinLifeCycleState.PREPROVISIONED ||
    props.dtwin.lifeCycleState === DtwinLifeCycleState.REGISTERED
  ) {
    showConfirm.value = true;
  }
};

async function updateDtwinLifeCycleState(
  newLifeCycleState: DtwinLifeCycleState,
) {
  if (!currentOrganization.value?.code) {
    throw new Error("Organization code is undefined");
  }

  isUpdatingLifeCycleState.value = true;
  showConfirm.value = false;

  const response = await dtwinsService.updateOrganizationDtwinLifeCycleState(
    currentOrganization.value.code,
    props.dtwin.uid,
    newLifeCycleState,
  );

  if (LxcError.check(response)) {
    response.notify(NotificationKey.error);
  } else {
    showNotificationSuccess(t(NotificationKey.success));
    emits("lifeCycleStateUpdated");
  }
  isUpdatingLifeCycleState.value = false;
}
</script>

<template>
  <!-- Context: device list on fleet => button remove the device from a fleet -->
  <lxc-button
    v-if="context === DtwinListDisplayContext.FLEET"
    html-type="button"
    type="borderless"
    :disabled="isRemovingFromFleet || !isAllowedToManageFleet"
    :icon="!isRemovingFromFleet ? ILxcLightTrash2 : undefined"
    :title="
      isAllowedToManageFleet
        ? t('dtwins.fleet.removeFromFleet')
        : t('fleet.actionForbidden')
    "
    class="invisible"
    @click.stop.prevent="$emit('removeFromFleet')"
  >
    <lxc-loader v-if="isRemovingFromFleet" />
  </lxc-button>

  <!-- Context: device list => button enable/disable -->
  <lxc-button
    v-else-if="
      context === DtwinListDisplayContext.DTWINS && canUpdateLifeCycleState
    "
    html-type="button"
    type="borderless"
    class="invisible !px-0 !py-0"
    :title="actionConfiguration.title"
    :disabled="isUpdatingLifeCycleState"
    :icon="!isUpdatingLifeCycleState ? actionConfiguration.icon : undefined"
    @click.stop.prevent="onTriggerAction"
  >
    <lxc-loader v-if="isUpdatingLifeCycleState" />
  </lxc-button>

  <!-- Context: device form => button enable/disable -->
  <lxc-button
    v-else-if="
      context === DtwinListDisplayContext.DTWIN_DETAIL &&
      canUpdateLifeCycleState
    "
    :title="actionConfiguration.title"
    type="tertiary"
    :icon="actionConfiguration.icon"
    :disabled="isUpdatingLifeCycleState"
    :is-loading="isUpdatingLifeCycleState"
    @click="onTriggerAction"
  >
    {{ actionConfiguration.content }}
  </lxc-button>

  <!-- Confirm modal on disable -->
  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t('dtwins.action.confirm.title')"
    :description="t('dtwins.action.confirm.description')"
    :cancel-label="t('button.cancel')"
    :ok-label="t('dtwins.action.confirm.disable')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @confirm="updateDtwinLifeCycleState(DtwinLifeCycleState.DISABLED)"
    @cancel="showConfirm = false"
  />
</template>

<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
